/**
 * The policy over the visitor trail on a public sales document.
 *
 * Its own file rather than a tail on `sales.ts`, which is already past the
 * length at which a second responsibility should move out (AGENTS.md §4). This
 * one answers a single screen: the retention card on Settings > Invoices.
 */
import { api } from "./api.js";

export interface ViewRetentionSettings {
  /** Does anything actually get scrubbed. Off on a desk that never asked. */
  enabled: boolean;
  /** How old a visit has to be before its address and browser go. */
  days: number;
}

export interface ViewRetentionDue {
  /** Rows the current age would take the trail off. */
  due: number;
  /** Rows still carrying a trail at all. The denominator for the sentence. */
  withTrail: number;
  /** The cutoff the count was taken at, ISO. */
  before: string;
}

export interface ViewRetentionState {
  settings: ViewRetentionSettings;
  due: ViewRetentionDue;
}

/** Mirrors the server's bounds, so the field refuses before the request does. */
export const MIN_VIEW_RETENTION_DAYS = 30;
export const MAX_VIEW_RETENTION_DAYS = 1095;

export const getViewRetention = () =>
  api.get<ViewRetentionState>("/admin/crm/sales/view-retention");

export const saveViewRetention = (body: Partial<ViewRetentionSettings>) =>
  api.put<ViewRetentionState>("/admin/crm/sales/view-retention", body);

/** The age, held to what the server will accept. */
export function clampRetentionDays(days: number): number {
  if (!Number.isFinite(days)) return MIN_VIEW_RETENTION_DAYS;
  return Math.min(MAX_VIEW_RETENTION_DAYS, Math.max(MIN_VIEW_RETENTION_DAYS, Math.round(days)));
}
